import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ApiResponse } from "@apiModels/ApiResponse.csharp";
import { CvUploadApi } from "../../Components/Api/CvUploadApi";
import { GenericModal } from "../../Components/BookingLegacy/FormElements/PopUps/GenericModal";
import classnames from "classnames";
import { cvFormDataValidationSchema } from "./CvFormDataValidation";
import { LoadingAnimation } from "../../Components/LoadingAnimation/LoadingAnimation";
import { CvUploadFormProps } from "./CvUploadFormProps.csharp";
import { FormInput } from "../../Components/Form/FormInput";

interface CvFormData {
  name: string;
  email: string;
  phoneNumber: string;
  files: FileList;
  gdpr: boolean;
}

export const CvUploadForm: React.FC<CvUploadFormProps> = ({
  namePlaceholder,
  emailPlaceholder,
  phoneNumberPlaceholder,
  inputRequiredValidation,
  emailValidation,
  phoneValidation,
  fileRequiredValidation,
  fileSizeValidation,
  submitButtonLabel,
  gdprLabel,
  gdprRequiredValidation,
  successModalDescription,
  successModalTitle,
  failedSentDescription,
  blockContentLinkId,
}) => {
  const [formSubmissionResult, setFormSubmissionResult] =
    useState<ApiResponse>();
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const {
    reset,
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<CvFormData>({
    resolver: yupResolver(
      cvFormDataValidationSchema({
        required: inputRequiredValidation,
        fileRequired: fileRequiredValidation,
        fileSize: fileSizeValidation,
        gdprRequired: gdprRequiredValidation,
        phone: phoneValidation,
        email: emailValidation,
      })
    ),
  });

  const cvUploadApi = new CvUploadApi();

  const onSubmit = async (data: CvFormData) => {
    setSubmitInProgress(true);
    try {
      const result = await cvUploadApi.uploadCv(buildFormData(data));
      setFormSubmissionResult(result);
    } catch (error) {
      setFormSubmissionResult({ isSuccess: false, messages: [] });
    }
    setSubmitInProgress(false);
  };

  const buildFormData = (data: CvFormData) => {
    const formData = new FormData();
    const file = data.files && data.files.item(0);
    data.name && formData.append("name", data.name);
    data.email && formData.append("email", data.email);
    data.phoneNumber && formData.append("phoneNumber", data.phoneNumber);
    formData.append("gdpr", data.gdpr.toString());
    file && formData.append("file", file);
    blockContentLinkId &&
      formData.append("blockContentLinkId", blockContentLinkId);
    return formData;
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="EPiServerForms"
        encType="multipart/form-data"
        name="CvUploadBookingForm"
      >
        <FormInput
          registerReturn={register("name")}
          placeholder={namePlaceholder}
          error={errors.name}
          type="textbox"
          disabled={submitInProgress}
        />
        <FormInput
          registerReturn={register("email")}
          placeholder={emailPlaceholder}
          error={errors.email}
          type="textbox"
          disabled={submitInProgress}
        />
        <FormInput
          registerReturn={register("phoneNumber")}
          placeholder={phoneNumberPlaceholder}
          error={errors.phoneNumber}
          type="number"
          disabled={submitInProgress}
        />
        <FormInput
          registerReturn={register("files")}
          error={errors.files}
          type="file"
          disabled={submitInProgress}
        />
        <FormInput
          registerReturn={register("gdpr")}
          placeholder={gdprLabel}
          error={errors.gdpr}
          type="checkbox"
          disabled={submitInProgress}
        />

        <button
          type="submit"
          disabled={submitInProgress}
          className={classnames(
            "FormSubmitButton",
            "FormSubmitButtonWithValidation"
          )}
        >
          {submitInProgress ? (
            <LoadingAnimation text={submitButtonLabel} />
          ) : (
            submitButtonLabel
          )}
        </button>
        {formSubmissionResult && !formSubmissionResult.isSuccess && (
          <span className="Form__Element__ValidationError">
            {failedSentDescription}
          </span>
        )}

        <GenericModal
          onClose={() => {
            reset();
            setFormSubmissionResult(undefined);
          }}
          isOpen={formSubmissionResult?.isSuccess ?? false}
          modalHeader={successModalTitle}
          modalDescription={successModalDescription}
        />
      </form>
    </div>
  );
};
